import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Spinner, EmptyState, Card, Table } from "@/components/common";
import { Activity, Award, Medal, Timer } from "lucide-react";

export default function Performance() {
  const [data, setData] = useState(null);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    api.get("/me/performance").then((r) => setData(r.data)).catch(() => setData({ records: [], leaderboard: [] }));
    api.get("/me/certificates").then((r) => setCerts(r.data)).catch(() => setCerts([]));
  }, []);
  if (!data) return <Spinner />;

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Performance" subtitle="Scores, results, rankings & certificates" />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Stat icon={Timer} label="Results recorded" value={data.records.length} />
        <Stat icon={Medal} label="Podium finishes" value={data.leaderboard.length} />
        <Stat icon={Award} label="Certificates" value={certs.length} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center gap-2"><Medal className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Rankings</h3></div>
          {data.leaderboard.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No ranked results yet.</p> : (
            <div className="space-y-2">
              {data.leaderboard.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2">
                  <div><p className="text-sm font-medium">{r.participant_name}</p><p className="text-xs text-muted-foreground">{r.metric} · {r.event_title}</p></div>
                  <span className="rounded-full bg-primary/15 px-2.5 py-1 font-mono text-xs font-semibold text-primary">{r.position}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center gap-2"><Activity className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Participation records</h3></div>
          {data.records.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No results recorded yet.</p> : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {data.records.map((r) => (
                <div key={r.id} className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm">
                  <div className="flex justify-between"><span className="font-medium">{r.participant_name}</span><span className="font-mono text-primary">{r.value}</span></div>
                  <p className="text-xs text-muted-foreground">{r.metric} · {r.event_title}{r.remarks ? ` · ${r.remarks}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <h3 className="mb-3 mt-6 font-display text-xl font-semibold">Certificates</h3>
      {certs.length === 0 ? (
        <EmptyState icon={Award} title="No certificates yet" text="Certificates appear here once completed events are certified." />
      ) : (
        <Table head={["Serial", "Participant", "Event", "Sport", "Issued", ""]}>
          {certs.map((c) => (
            <tr key={c.id} data-testid={`perf-cert-${c.id}`} className="border-b border-border/60 last:border-0 hover:bg-background/40">
              <td className="px-4 py-3 font-mono text-xs text-primary">{c.serial}</td>
              <td className="px-4 py-3 font-medium">{c.participant_name}</td>
              <td className="px-4 py-3 text-muted-foreground">{c.event_title}</td>
              <td className="px-4 py-3 text-muted-foreground">{c.sport}</td>
              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{(c.issued_at || "").slice(0, 10)}</td>
              <td className="px-4 py-3"><Link to={`/certificate/${c.id}`} target="_blank" className="rounded-lg bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/25">View</Link></td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /><p className="font-mono text-xs uppercase text-muted-foreground">{label}</p></div>
      <p className="font-display text-3xl font-bold">{value}</p>
    </Card>
  );
}
